import * as Bitmovin from 'bitmovin-player'
import { Subject, Subscription } from 'rxjs'
import Http from './backend/http'
import Debug from './core/debug'
import Device from './core/device'
import Native, { ReceivedEvent, SentEvent } from './native/native'
import Playback from './playback/playback'
import { StreamingProtocol } from './storage/data-model/source/storage-data-model-source'
import Storage from './storage/storage'
import Theme, { ThemeOptions } from './theme/theme'

/**
 * The playback options
 */
export interface PlaybackOptions {
    /**
     * Whether or not to autoplay the video source
     */
    autoplay?: boolean

    /**
     * The volume of the video player
     */
    volume?: number
}

/**
 * The mount options for the player.
 * 
 * @author Stan Hurks
 */
export interface PlayerMountOptions {
    /**
     * The container element in which to inject the player
     */
    container: HTMLElement

    /**
     * The theme
     */
    theme?: ThemeOptions

    /**
     * Playback options
     */
    playback?: PlaybackOptions

    /**
     * Whether or not the player is loaded from a native environment
     */
    native?: boolean

    /**
     * All options for the web player
     */
    web?: {
        /**
         * The bitmovin license keys to use
         */
        keys: {
            /**
             * The key for the player functionality
             */
            player: string

            /**
             * The key for the analytics functionality
             */
            analytics: string
        }
    }
}

/**
 * The type of data to be passed to the `Player.destroy` RxJS subject.
 * 
 * @author Stan Hurks
 */
export type PlayerDestroyData = number | 'all'

/**
 * The configuration for loading a source into the player.
 * 
 * @author Stan Hurks
 */
export type PlayerSourceConfig = Bitmovin.SourceConfig & {
    /**
     * Product ID of the stream
     */
    prid: string

    /**
     * The streaming protocol to request
     */
    protocol: StreamingProtocol
}

/**
 * The main class for the player.
 * 
 * @author Stan Hurks
 */
export default class Player {
    /**
     * The list of players
     */
    public static readonly instances: Player[] = []

    /**
     * The class prefix for the container
     */
    public static readonly classPrefix: string = 'npo-player-'

    /**
     * Whether or not the player is loaded from a native environment (ios/android)
     */
    public static isNative: boolean = false

    /**
     * The amount of players instantiated.
     */
    private static counter: number = 0

    /**
     * Destroys a player by id, or all players.
     * 
     * The `this.onDestroy` function uses a `setTimeout` function to wait for
     * all utility classes to be destroyed, before executing.
     * 
     * This means that after calling this method you will need to wrap the code after
     * it into 2 seperate `setTimeout` statements. If you don't, the video player won't be
     * destroyed in that code during execution.
     */
    public static readonly destroy: Subject<PlayerDestroyData> = new Subject()

    /**
     * The player instance
     */
    public readonly player!: Bitmovin.PlayerAPI

    /**
     * The player mount options
     */
    public readonly playerMountOptions!: PlayerMountOptions

    /**
     * The theme of the player
     */
    public readonly theme!: Theme

    /**
     * The playback details
     */
    public readonly playback!: Playback

    /**
     * The ID
     */
    public readonly id!: number

    /**
     * The subscription to the `Player.destroy` RxJS subject.
     */
    private readonly subscriptionDestroy!: Subscription

    constructor(options: PlayerMountOptions) {
        if (options.native) {
            Player.isNative = true
            Native.log(`Initializing player instance... (#${++Player.counter})`)
        } else {
            console.debug(`Player mounting... (#${++Player.counter})`)
        }

        // Set ID
        this.id = Player.counter

        // Add class to container
        options.container.classList.add(`${Player.classPrefix}${this.id}`)

        // Initialize player and utility classes
        if (options.web) {
            this.player = new Bitmovin.Player(options.container, {
                key: options.web.keys.player,
                playback: {
                    volume: options.playback && options.playback.volume !== undefined
                        ? options.playback.volume
                        : Storage.data.playback.volume,
                    autoplay: options.playback && options.playback.autoplay !== undefined
                        ? options.playback.autoplay
                        : Storage.data.playback.autoplay
                },
                analytics: {
                    key: options.web.keys.analytics
                },
                ...(Storage.data.playback.forwardDuration)
                && {
                    buffer: {
                        [Bitmovin.MediaType.Video]: {
                            [Bitmovin.BufferType.ForwardDuration]: Storage.data.playback.forwardDuration
                        },
                        [Bitmovin.MediaType.Audio]: {
                            [Bitmovin.BufferType.ForwardDuration]: Storage.data.playback.forwardDuration
                        }
                    }
                }
            })
            this.playerMountOptions = options
        }
        else {
            if (options.playback) {
                if (options.playback.volume !== undefined) {
                    if (options.playback.volume === 0) {
                        Native.broadcast('mute')
                    } else {
                        Native.broadcast('unmute')
                    }
                }
                if (options.playback.autoplay) {
                    Native.broadcast('play')
                }
            }
        }
        this.theme = new Theme(options.theme || {}, this.id)
        this.playback = new Playback(this.id)

        // Events
        if (options.native) {
            Native.on('muted', () => {
                Playback.volumeChanged.next(0)
            })
            Native.on('unmuted', () => {
                Playback.volumeChanged.next(100)
            })
        }
        else {
            this.player.on(Bitmovin.PlayerEvent.VolumeChanged, (data) => {
                Playback.volumeChanged.next(this.player.getVolume())
            })
            this.player.on(Bitmovin.PlayerEvent.Unmuted, (data) => {
                Playback.volumeChanged.next(this.player.getVolume())
            })
            this.player.on(Bitmovin.PlayerEvent.Muted, (data) => {
                Playback.volumeChanged.next(0)
            })
        }

        // Subscriptions
        this.subscriptionDestroy = Player.destroy.subscribe(this.onDestroy)

        // Add to instances
        Player.instances.push(this)
    }

    /**
     * Destroy the player
     * 
     * @param data The data passed to the RxJS subject
     */
    public onDestroy = (data: PlayerDestroyData) => {
        // Only destroy the player when the `all` option is selected,
        // or when the id in data matches that of the current player.
        if (data !== 'all' && data !== this.id) {
            return
        }

        // All related classes may also listen to this same RxJS subject.
        // In order to destroy everything at the right time, the program will
        // wait for the current cycle in the javascript thread to end.
        setTimeout(() => {
            // Remove class from container
            this.player.getContainer().classList.remove(`${Player.classPrefix}${this.id}`)
    
            // Destroy the bitmovin player
            this.player.destroy()
    
            // Remove from instances
            for (let i = 0; i < Player.instances.length; i ++) {
                let player = Player.instances[i]
                if (player.id === this.id) {
                    Player.instances.splice(i, 1)
                    break
                }
            }

            // Unsubscribe the subscriptions
            this.subscriptionDestroy.unsubscribe()

            console.debug(`Player destroyed. (#${this.id})`)
        })
    }

    /**
     * Load a source for the player.
     * 
     * Will clear HLS and DASH properties, when progressive is defined.
     * 
     * @param sourceConfig the config for the source to be loaded
     */
    public load = (sourceConfig: PlayerSourceConfig): Promise<void> => {
        return new Promise((resolve, reject) => {
            if (sourceConfig.prid.length) {
                if (sourceConfig.protocol !== 'auto' || (Device.protocol !== null && Device.drm !== null)) {
                    Http.post<{
                        /**
                         * The URL to the resource
                         */
                        url: string,
    
                        /**
                         * The DRM token
                         */
                        drmToken: string

                        /**
                         * The URL to the subtitles manifest
                         */
                        subtitles: string

                        /**
                         * The URL to the thumbnail manifest
                         */
                        thumbnails: string
                    }>('stream-link', {
                        providerName: 'npo',
                        platformName: 'npoplus',
                        profileName: sourceConfig.protocol === 'auto'
                            ? Device.protocol
                            : sourceConfig.protocol === 'dash_u'
                                ? 'dash_unencrypted'
                                : sourceConfig.protocol,
                        streamId: sourceConfig.prid
                    }).then((data) => {
                        this.player.load({
                            ...sourceConfig,
    
                            ...sourceConfig.protocol === 'auto'
                            ? {
                                dash: ['dash', 'dash_u'].includes(Device.protocol!)
                                    ? data.data.url
                                    : undefined,
                                hls: Device.protocol! === 'hls'
                                    ? data.data.url
                                    : undefined,
                                progressive: Device.protocol! === 'progressive'
                                    ? data.data.url
                                    : undefined,
                                smooth: Device.protocol! === 'smooth'
                                    ? data.data.url
                                    : undefined,
                                ...data.data.drmToken
                                && {
                                    drm: {
                                        ...Device.drm === 'widevine'
                                        ? {
                                            widevine: {
                                                LA_URL: `https://npo-drm-gateway.samgcloud.nepworldwide.nl/authentication?custom_data=${data.data.drmToken}`
                                            } 
                                        }
                                        :
                                        Device.drm === 'fairplay'
                                        ? {
                                            fairplay: {
                                                LA_URL: `https://npo-drm-gateway.samgcloud.nepworldwide.nl/authentication?custom_data=${data.data.drmToken}`,
                                                certificateURL: 'https://s3-eu-west-1.amazonaws.com/24i-npo-stream/fairplay.cer'
                                            }
                                        }
                                        : {
                                            playready: {
                                                LA_URL: `https://npo-drm-gateway.samgcloud.nepworldwide.nl/authentication?custom_data=${data.data.drmToken}`
                                            }
                                        }
                                    }
                                }
                            }
                            : {
                                dash: ['dash', 'dash_u'].includes(sourceConfig.protocol)
                                    ? data.data.url
                                    : undefined,
                                hls: sourceConfig.protocol === 'hls'
                                    ? data.data.url
                                    : undefined,
                                progressive: sourceConfig.protocol === 'progressive'
                                    ? data.data.url
                                    : undefined,
                                smooth: sourceConfig.protocol === 'smooth'
                                    ? data.data.url
                                    : undefined,
                                ...data.data.drmToken
                                && {
                                    drm: {
                                        widevine: {
                                            LA_URL: `https://npo-drm-gateway.samgcloud.nepworldwide.nl/authentication?custom_data=${data.data.drmToken}`
                                        } 
                                    }
                                }
                            },
                            analytics: {
                                key: this.playerMountOptions.web?.keys.analytics,
                                // TODO: video ID?
                                videoId: sourceConfig.title,
                                title: sourceConfig.title,
                                cdnProvider: this.generateCDNString(data.data.url)
                            },
                            subtitleTracks: [
                                ...sourceConfig.subtitleTracks || [],
                                ...data.data.subtitles ? [{
                                    lang: 'nl_NL',
                                    kind: 'subtitle',
                                    id: 'nl_NL',
                                    label: 'Nederlands'
                                }] : [],
                                ...Debug.initialized
                                ?
                                [{
                                    lang: 'default',
                                    kind: 'subtitle',
                                    id: 'default',
                                    label: 'Default (Storage)',
                                    url: Storage.data.source.subtitleURL
                                }] : []
                            ],
                            ...data.data.thumbnails
                            && {
                                thumbnailTrack: {
                                    url: data.data.thumbnails
                                }
                            }
                        })
                        .then(() => {
                            console.debug(`Loaded video source. (#${this.id})`)
                            setTimeout(() => {
                                // TODO: niet automatisch, of wel?
                                this.player.subtitles.enable('nl', true)
                            })
                            return resolve()
                        })
                        .catch((error) => {
                            console.error(`Error while loading source (#${this.id}):\n`, error)
                            return reject(error)
                        })
                    }).catch((error) => {
                        console.error(error)
                        return reject(error)
                    })
                }
                else {
                    // TODO: all console.log's with chalk
                    // TODO: error messages in the player
                    console.error(`Could not load source: browser or operating system is not supported.`)
                    console.error(`Browser: ${Device.browser}`)
                    console.error(`Operating system: ${Device.os}`)
                    return resolve()
                }
            }
            else {
                console.log(`Could not load source: The given PRID is an empty string. (#${this.id})`)
                return resolve()
            }
        })
    }

    /**
     * Generate a CDN string from a video source url.
     * 
     * @param sourceURL The url of the video source
     */
    private generateCDNString = (sourceURL: string): string => {
        // list of CDN providers
        const npoCdnProviders: Record<string, string> = {};
        npoCdnProviders['nep.global.ssl.fastly.net'] = 'NEP'
        npoCdnProviders['cdn.streamgate.nl'] = 'NEP'
        npoCdnProviders['npo-fsly.cdn.streamgate.io'] = 'FASTLY'
        npoCdnProviders['cdn.eurovisioncdn.net'] = 'EUROVISION'
        npoCdnProviders['npo.prd.cdn.bcms.kpn.com'] = 'KPN'
        npoCdnProviders['cdn.bcms.kpn.com'] = 'KPN'
        npoCdnProviders['Akamai'] = 'AKEMAI'
        npoCdnProviders['icecast.omroep.nl'] = 'NPO'
        npoCdnProviders['content.omroep.nl'] = 'NPO'
        npoCdnProviders['podcast.npo.nl'] = 'NPO'
        npoCdnProviders['video.omroep.nl'] = 'NPO'

        // Set the CDN string
        var cdnString = sourceURL
        for (const npoCdnProviderUrl of Object.keys(npoCdnProviders)) {
            if (sourceURL.includes(npoCdnProviderUrl)) {
                cdnString = npoCdnProviders[npoCdnProviderUrl]
                break
            }
        }

        return cdnString
    }
}